import {UserService} from 'store/apiClients/user';
import {ProfileStatuses} from 'common/profile/enums';
import {makeAutoObservable} from 'mobx';
import {AxiosResponse} from 'axios';
import {Status} from 'constants/status';
import {UpdateParams, UpdateProfileParams} from 'store/apiClients/user/types';
import {Pagination} from 'store/utilityClasses/Pagination';
import {CandidatesFilters, OperatorType, ProfileWithUser, User} from './types';


export class UserStore {
  private userService: UserService;

  operators: OperatorType[] | null = null;
  users = new Map<number, User>();
  statusesById = new Map<number, Status>();

  status = Status.Initial;
  pagination = new Pagination();
  supervisorCandidates = [];
  candidatesFilters: CandidatesFilters = {
    fio: '',
    userId: '',
    email: '',
    isSupervisor: false,
    status: null,
  }

  get isLoading(): boolean {
    return this.status === Status.Pending;
  }

  constructor() {
    this.userService = new UserService();
    makeAutoObservable(this, {}, { autoBind: true });
  }

  getUserById = (userId: number): User | null => this.users.get(userId) || null;

  * fetchUserById(userId: number): Generator {
    try {
      this.statusesById.set(userId, Status.Pending);
      const { data } = (yield this.userService.getUserById(userId)) as AxiosResponse<User>;
      this.setUsersFromApi([data]);
      this.statusesById.delete(userId);
    } catch (e) {
      this.statusesById.set(userId, Status.Rejected);
    }
  }

  * fetchOperators(status: ProfileStatuses): Generator<unknown, AxiosResponse> {
    try {
      const response = (yield this.userService.getOperators(status)) as AxiosResponse<OperatorType[]>;
      this.operators = response.data;
      return response;
    } catch (e) {
      throw new Error(e);
    }
  }

  * fetchSupervisorCandidates(): Generator {
    try {
      this.status = Status.Pending;
      const {fio, isSupervisor, userId, status, email} = this.candidatesFilters;

      const { data, headers } = yield this.userService.getSupervisorCandidates({
        ...(fio && {fio}),
        ...(userId && {userId}),
        ...(email && {email}),
        ...(isSupervisor && {isSuperviser: isSupervisor}),
        ...(status && {status}),
        page: this.pagination.page,
        perPage: 50
      }) as AxiosResponse<User[]>;
      this.supervisorCandidates = data;
      this.pagination.setPages(headers);
      this.status = Status.Fulfilled;
    } catch (err) {
      this.status = Status.Rejected;
      console.error(err);
    }
  }

  * changeSupervisorStatus(userId: number, isSupervisor: boolean): Generator {
    try {
      this.status = Status.Pending;
      yield this.userService.changeSupervisorStatus(userId, isSupervisor);
      this.status = Status.Fulfilled;
    } catch (err) {
      this.status = Status.Rejected;
      console.error(err);
    }
  }

  * changeStatus(userId: number, status: ProfileStatuses): Generator {
    try {
      yield this.userService.changeStatus(userId, status);
      yield this.fetchOperators(ProfileStatuses.waiting);
    } catch (e) {
      throw new Error(e);
    }
  }

  * fetchUpdateProfile(profile: UpdateProfileParams): Generator<unknown, AxiosResponse<ProfileWithUser> | boolean> {
    try {
      return (yield this.userService.updateProfile(profile)) as AxiosResponse<ProfileWithUser>;
    } catch (e) {
      return false;
    }
  }

  * updateUserData(params: UpdateParams): Generator<unknown, AxiosResponse> {
    try {
      return (yield this.userService.update(params)) as AxiosResponse;
    } catch (e) {
      throw new Error(e);
    }
  }

  updateCandidatesFiltersValue(value: string | boolean | ProfileStatuses, type: keyof CandidatesFilters): void {
    this.candidatesFilters[type] = value;

    // для текстовых полей выполняем запрос, если ввыедено, как минимум, 3 символа
    if (
        ['fio', 'email'].includes(type) && ((value as string).length > 2 || !(value as string).length)
        || !['fio', 'email'].includes(type)
    ) {
      this.fetchSupervisorCandidates();
    }
  }

  resetCandidatesFilters(): void {
    this.candidatesFilters.userId = '';
    this.candidatesFilters.fio = '';
    this.candidatesFilters.email = '';
    this.candidatesFilters.isSupervisor = false;
    this.candidatesFilters.status = null;

    this.fetchSupervisorCandidates();
  }

  private setUsersFromApi(apiData: User[]): void {
    apiData.forEach((user) => {
      this.users.set(user.id, user);
    });
  }
}
